import { useCallback, useEffect, useState } from "react";
import { supabase, rowToCamel, rowToSnake } from "@/lib/supabase";

/**
 * Supabase-backed collection hook. Mirrors the shape of the old localStorage
 * `useCollection` hook (items / add / update / remove) but reads and writes to
 * a real database table, so the admin and public site share one source of truth.
 *
 * On first load, if the table is empty, it seeds it from the provided mock data
 * (idempotent upsert) so the site never appears blank.
 */
export function useSupabaseTable<T extends { id: string }>(table: string, seed: T[]) {
  const [items, setItems] = useState<T[]>(seed);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data, error: err } = await supabase
          .from(table)
          .select("*")
          .order("created_at", { ascending: false });

        if (err) throw err;

        if (!active) return;

        if (data && data.length > 0) {
          setItems(data.map((r) => rowToCamel<T>(r)));
        } else if (seed.length > 0) {
          const rows = seed.map((s) =>
            rowToSnake(s as unknown as Record<string, unknown>),
          );
          await supabase.from(table).upsert(rows);
          setItems(seed);
        } else {
          setItems([]);
        }
      } catch (e) {
        if (active) {
          setError(e instanceof Error ? e.message : "Failed to load data");
          setItems(seed);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // seed is a stable module-level constant from mocks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const add = useCallback(
    async (item: T) => {
      setItems((prev) => [item, ...prev]);
      try {
        await supabase.from(table).upsert(rowToSnake(item as unknown as Record<string, unknown>));
      } catch {
        // optimistic update already applied; surface error on next reload
      }
    },
    [table],
  );

  const update = useCallback(
    async (id: string, patch: Partial<T>) => {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
      try {
        await supabase
          .from(table)
          .update(rowToSnake(patch as Record<string, unknown>))
          .eq("id", id);
      } catch {
        // ignore — keep local state responsive
      }
    },
    [table],
  );

  const remove = useCallback(
    async (id: string) => {
      setItems((prev) => prev.filter((it) => it.id !== id));
      try {
        await supabase.from(table).delete().eq("id", id);
      } catch {
        // ignore
      }
    },
    [table],
  );

  return { items, loading, error, add, update, remove };
}